import { useEffect, useState } from "react";
import { Loader } from "@/components/common/loader";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiFetch } from "@/lib/api";
import { formatCents } from "@/lib/utils";

interface ScheduleRow {
  month: string;
  amountCents: number;
  totalCents: number;
  balanceCents: number;
  interestCents: number;
}

interface Props {
  caseId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Step 2 — IA Payment Schedule modal. Read-through of the Tier 2.2 amortization
 * schedule (the "consider an IA instead" context). PDF/Excel export is a
 * deferred Tier 2.2 seam (the export endpoints aren't built yet).
 */
export function PaymentScheduleModal({ caseId, open, onOpenChange }: Props) {
  const [rows, setRows] = useState<ScheduleRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setRows(null);
    setError(null);
    apiFetch<{ data: { available: boolean; rows: ScheduleRow[] } }>(
      `/cases/${caseId}/oic/schedule/`,
    )
      .then((res) => !cancelled && setRows(res.data.rows))
      .catch(() => !cancelled && setError("Couldn't load the payment schedule."));
    return () => {
      cancelled = true;
    };
  }, [open, caseId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Installment payment schedule</DialogTitle>
          <DialogDescription>
            Estimated amortization at the IRS interest rate (Tier 2.2). Export to
            PDF/Excel arrives with the worksheet export module.
          </DialogDescription>
        </DialogHeader>
        {error ? (
          <p className="text-destructive text-sm">{error}</p>
        ) : rows == null ? (
          <Loader className="my-8" />
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">No schedule to show.</p>
        ) : (
          <div className="max-h-96 overflow-y-auto overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Payment</TableHead>
                  <TableHead className="text-right">Interest</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.month}>
                    <TableCell>{r.month}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCents(r.amountCents)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCents(r.interestCents)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCents(r.balanceCents)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
